import { useEffect, useState } from 'react';
import { supabase } from '../../services/supabase';
import { useCreatorSession } from './creatorSession';

/**
 * Is a member of staff looking at this workspace, rather than the creator?
 *
 * Two ways staff get here, and each is caught by a different signal:
 *
 *  1. A link carrying the admin's own `?userId=`. creatorSession verifies that
 *     row (`forwardedAdmin.user`, only set when `users.user_type = 'admin'`) —
 *     the same check the telemetry fix (f0a4f45) settled on.
 *  2. Talent Management's "view as", which signs in with a session minted by
 *     admin-impersonate and forwards the CREATOR's id, so (1) never fires.
 *     admin-impersonate records every session it mints in
 *     staff_impersonation_sessions, and current_impersonation() reports it.
 *
 * The database enforces (2) on its own — replies and read receipts are refused
 * for a recorded session — so this hook is what keeps the UI honest and what
 * labels telemetry. Errors resolve to "not viewing": a creator must never lose
 * the ability to reply because a lookup failed.
 */
export type StaffViewing = {
  viewing: boolean;
  adminId: string | null;
  adminName: string | null;
  /** False until the impersonation lookup has answered. */
  resolved: boolean;
};

type Impersonation = { adminId: string; adminName: string | null } | null;

// One lookup per access token: every screen asking gets the same answer, and a
// new sign-in (new token) asks again.
let cached: { token: string; promise: Promise<Impersonation> } | null = null;

export async function lookupImpersonation(): Promise<Impersonation> {
  const { data: auth } = await supabase.auth.getSession();
  const token = auth.session?.access_token;
  if (!token) return null;
  if (cached?.token !== token) {
    cached = {
      token,
      promise: (async () => {
        const { data, error } = await supabase.rpc('current_impersonation');
        if (error) return null;
        const row = Array.isArray(data) ? data[0] : data;
        return row?.admin_user_id ? { adminId: row.admin_user_id, adminName: row.admin_name ?? null } : null;
      })().catch(() => null),
    };
  }
  return cached.promise;
}

export function useStaffViewing(): StaffViewing {
  const { status, forwardedAdmin } = useCreatorSession();
  const verifiedAdmin = forwardedAdmin.user;
  const [impersonation, setImpersonation] = useState<Impersonation | undefined>(undefined);

  useEffect(() => {
    if (status !== 'ready' || verifiedAdmin) return undefined;
    let cancelled = false;
    void lookupImpersonation().then((result) => {
      if (!cancelled) setImpersonation(result);
    });
    return () => {
      cancelled = true;
    };
  }, [status, verifiedAdmin]);

  if (verifiedAdmin) {
    return { viewing: true, adminId: verifiedAdmin.id, adminName: forwardedAdmin.email, resolved: true };
  }
  if (impersonation) {
    return { viewing: true, adminId: impersonation.adminId, adminName: impersonation.adminName, resolved: true };
  }
  return { viewing: false, adminId: null, adminName: null, resolved: impersonation !== undefined };
}
